import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
    Activity,
    Eye,
    MousePointerClick,
    TrendingUp,
    BarChart3,
    ArrowUpRight,
    ArrowDownRight,
    MessageSquare,
    Target,
    CheckCircle,
} from 'lucide-react';

import { PRIMARY_BG } from '../../theme/brand';
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const weeklyData = [
    { date: 'Mon', views: 6234, taps: 1523, engagement: 421 },
    { date: 'Tue', views: 7456, taps: 1876, engagement: 534 },
    { date: 'Wed', views: 6892, taps: 1654, engagement: 478 },
    { date: 'Thu', views: 8123, taps: 2145, engagement: 621 },
    { date: 'Fri', views: 7654, taps: 1987, engagement: 567 },
    { date: 'Sat', views: 5432, taps: 1234, engagement: 389 },
    { date: 'Sun', views: 4832, taps: 1098, engagement: 312 },
];

const completionSegments = [
    { segment: '0-25%',   users: 8234 },
    { segment: '25-50%',  users: 6543 },
    { segment: '50-75%',  users: 4321 },
    { segment: '75-100%', users: 2987 },
];

const topStories = [
    { name: 'Product Launch Teaser',   views: 12456, engagement: 34.2, channel: 'WhatsApp' },
    { name: 'Flash Sale Announcement', views: 10234, engagement: 31.8, channel: 'Instagram' },
    { name: 'Behind The Scenes',       views: 8765,  engagement: 28.5, channel: 'WhatsApp' },
    { name: 'Customer Testimonial',    views: 7543,  engagement: 26.1, channel: 'WhatsApp' },
    { name: 'New Collection Preview',  views: 6789,  engagement: 23.7, channel: 'Instagram' },
];

const channelDist = [
    { name: 'WhatsApp Status',   count: 856, pct: 68.6, color: '#10b981' },
    { name: 'Instagram Stories', count: 321, pct: 25.7, color: '#ec4899' },
    { name: 'Facebook Stories',  count: 70,  pct: 5.7,  color: '#3b82f6' },
];

const engagementBreakdown = [
    { label: 'Taps Forward', value: 8234, pct: 65, icon: ArrowUpRight,     color: '#3b82f6' },
    { label: 'Taps Back',    value: 3421, pct: 38, icon: ArrowDownRight,   color: '#f97316' },
    { label: 'Exits',        value: 1876, pct: 22, icon: MousePointerClick, color: '#ef4444' },
    { label: 'Replies',      value: 542,  pct: 15, icon: MessageSquare,    color: '#22c55e' },
];

const gridOptions = {
    color: '#e2e8f0',
    borderDash: [4, 4],
};

const barChartData = {
    labels: weeklyData.map(d => d.date),
    datasets: [
        {
            label: 'Views',
            data: weeklyData.map(d => d.views),
            backgroundColor: '#8b5cf6',
            borderRadius: 3,
            barPercentage: 0.7,
        },
        {
            label: 'Taps',
            data: weeklyData.map(d => d.taps),
            backgroundColor: '#3b82f6',
            borderRadius: 3,
            barPercentage: 0.7,
        },
        {
            label: 'Engagement',
            data: weeklyData.map(d => d.engagement),
            backgroundColor: '#10b981',
            borderRadius: 3,
            barPercentage: 0.7,
        },
    ],
};

const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: { mode: 'index', intersect: false },
    },
    scales: {
        x: { grid: gridOptions, ticks: { font: { size: 11 } } },
        y: { grid: gridOptions, ticks: { font: { size: 11 } }, beginAtZero: true },
    },
};

const lineChartData = {
    labels: completionSegments.map(d => d.segment),
    datasets: [
        {
            label: 'Users',
            data: completionSegments.map(d => d.users),
            borderColor: '#8b5cf6',
            backgroundColor: 'rgba(139,92,246,0.08)',
            pointBackgroundColor: '#8b5cf6',
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0,
            fill: false,
        },
    ],
};

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: { mode: 'index', intersect: false },
    },
    scales: {
        x: { grid: gridOptions, ticks: { font: { size: 11 } } },
        y: { grid: gridOptions, ticks: { font: { size: 11 } }, beginAtZero: true },
    },
};

export default function StoryInsightsTab() {
    return (
        <div style={{ position: 'relative' }}>
            {/* Coming Soon overlay */}
            <div style={{
                position: 'absolute',
                inset: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                backgroundColor: 'rgba(255,255,255,0.55)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                borderRadius: '12px',
            }}>
                <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    letterSpacing: '-0.5px',
                }}>
                    Coming Soon
                </div>
                <div style={{
                    fontSize: '0.95rem',
                    color: '#64748b',
                    fontWeight: '400',
                }}>
                    Story Insights are under development
                </div>
            </div>
        <div>
            <div className="mb-5">
                <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--t-text)' }}>Story Insights</h1>
                <p className="text-sm" style={{ color: 'var(--t-muted)' }}>
                    Track performance, engagement, and completion rates across all published stories.
                </p>
            </div>

            {/* Key metric cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                    { icon: Activity,           label: 'Total Stories',       value: '1,247',  sub: '+12.5% from last period', color: '#8b5cf6' },
                    { icon: Eye,                label: 'Total Views',          value: '45,623', sub: '+18.3% from last period', color: '#3b82f6' },
                    { icon: MousePointerClick,  label: 'Engagement Rate',      value: '24.8%',  sub: '+3.2% from last period',  color: '#f59e0b' },
                    { icon: TrendingUp,         label: 'Click-Through Rate',   value: '8.4%',   sub: '+1.7% from last period',  color: '#10b981' },
                ].map(({ icon: Icon, label, value, sub, color }) => (
                    <div key={label} className="rounded-xl p-4" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, color }}>
                                <Icon size={16} />
                            </div>
                        </div>
                        <p className="text-2xl font-bold" style={{ color: 'var(--t-text)' }}>{value}</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--t-muted)' }}>{label}</p>
                        <p className="text-[11px] mt-1" style={{ color: '#10b981' }}>{sub}</p>
                    </div>
                ))}
            </div>

            {/* Stories Performance Bar Chart */}
            <div className="rounded-xl p-5 mb-6" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                <div className="flex items-center gap-2 mb-1">
                    <BarChart3 size={16} style={{ color: '#8b5cf6' }} />
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Stories Performance Overview</h3>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--t-muted)' }}>Views, taps, and engagement over time</p>
                <div style={{ height: 400 }}>
                    <Bar data={barChartData} options={barChartOptions} />
                </div>
                <div className="flex gap-5 mt-4">
                    {[{ c: '#8b5cf6', l: 'Views' }, { c: '#3b82f6', l: 'Taps' }, { c: '#10b981', l: 'Engagement' }].map(({ c, l }) => (
                        <div key={l} className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm" style={{ background: c }} />
                            <span className="text-xs" style={{ color: 'var(--t-text2)' }}>{l}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Channel dist + Top stories */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">

                {/* Stories by Channel */}
                <div className="rounded-xl p-5" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <BarChart3 size={16} style={{ color: '#3b82f6' }} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Stories by Channel</h3>
                    </div>
                    <p className="text-xs mb-4" style={{ color: 'var(--t-muted)' }}>Distribution across platforms</p>
                    <div className="space-y-3">
                        {channelDist.map(ch => (
                            <div key={ch.name} className="flex items-center justify-between p-3 rounded-lg" style={{ border: '1px solid var(--t-border)' }}>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: ch.color }} />
                                    <span className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>{ch.name}</span>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold" style={{ color: 'var(--t-text)' }}>{ch.count}</p>
                                    <p className="text-[11px]" style={{ color: 'var(--t-muted)' }}>{ch.pct}%</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top Performing Stories */}
                <div className="rounded-xl p-5" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={16} style={{ color: '#10b981' }} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Top Performing Stories</h3>
                    </div>
                    <p className="text-xs mb-4" style={{ color: 'var(--t-muted)' }}>Stories with highest engagement</p>
                    <div className="space-y-2.5">
                        {topStories.map((s, i) => (
                            <div key={s.name} className="flex items-center gap-3 p-3 rounded-lg" style={{ border: '1px solid var(--t-border)' }}>
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                                    style={{ background: '#8b5cf618', color: '#8b5cf6' }}>{i + 1}</div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--t-text)' }}>{s.name}</p>
                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className="text-[11px]" style={{ color: 'var(--t-muted)' }}>{s.views.toLocaleString()} views</span>
                                        <span className="text-[11px] font-semibold" style={{ color: '#10b981' }}>{s.engagement}% engagement</span>
                                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{ background: '#8b5cf618', color: '#8b5cf6' }}>{s.channel}</span>
                                    </div>
                                </div>
                                <Eye size={15} style={{ color: 'var(--t-muted)', flexShrink: 0 }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Engagement Breakdown */}
            <div className="rounded-xl p-5 mb-6" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Activity size={16} style={{ color: '#06b6d4' }} />
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Engagement Breakdown</h3>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--t-muted)' }}>How users interact with your stories</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {engagementBreakdown.map(({ label, value, pct, icon: Icon, color }) => (
                        <div key={label} className="p-4 rounded-lg" style={{ border: '1px solid var(--t-border)' }}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium" style={{ color: 'var(--t-text2)' }}>{label}</span>
                                <Icon size={14} style={{ color }} />
                            </div>
                            <p className="text-xl font-bold mb-2.5" style={{ color: 'var(--t-text)' }}>{value.toLocaleString()}</p>
                            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PRIMARY_BG }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Story Completion Rate Line Chart */}
            <div className="rounded-xl p-5" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Target size={16} style={{ color: '#8b5cf6' }} />
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Story Completion Rate</h3>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--t-muted)' }}>Percentage of users who view stories to completion</p>
                <div style={{ height: 400 }}>
                    <Line data={lineChartData} options={lineChartOptions} />
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-lg mt-4" style={{ background: '#8b5cf610', border: '1px solid #8b5cf630' }}>
                    <CheckCircle size={16} style={{ color: '#8b5cf6' }} />
                    <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Average Completion Rate: 67.3%</p>
                        <p className="text-xs mt-0.5" style={{ color: '#8b5cf6' }}>2,987 users viewed stories to completion</p>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}
